/**
 * Cart Drawer V2 — live updates
 * -----------------------------
 * Strategy: server-side re-render via Section Rendering API, then *in-place*
 * value updates (no element replacement) so CSS transitions can animate
 * smoothly between states.
 *
 * On every cart change theme.dev.js dispatches `theme:cart:update` (or
 * `theme:cart:added`). We then fetch /cart?section_id=api-cart-items —
 * the same URL the theme already fetches, so the browser cache dedupes —
 * parse the response, and patch values into the existing DOM elements.
 *
 * Why in-place rather than replaceWith():
 *   1. The progress fill bar's CSS `transition: width 300ms ease` only fires
 *      when an existing element's width changes. Replacing the element makes
 *      it appear at the new value instantly with no animation.
 *   2. Element replacement causes a one-frame DOM gap → visible flash.
 *   3. Milestone dots, label colors etc. all animate via CSS transitions
 *      that need the same DOM node to persist.
 *
 * Why server-side re-render: /cart.js does not expose product.metafields, so
 * JS cannot accurately compute swimCount when the merchant uses
 * product.metafields.custom.type instead of product_type. Liquid can.
 */

(function () {
  'use strict';

  // Same URL theme.dev.js uses, so HTTP cache should serve both calls.
  const SECTION_URL = '/cart?section_id=api-cart-items';

  // Inflight dedupe so rapid cart changes don't stack fetches.
  let inflight = null;

  function fetchSection() {
    if (inflight) return inflight;
    inflight = fetch(SECTION_URL, { credentials: 'same-origin' })
      .then(r => { if (!r.ok) throw new Error('cart section ' + r.status); return r.text(); })
      .then(html => new DOMParser().parseFromString(html, 'text/html'))
      .finally(() => { inflight = null; });
    return inflight;
  }

  // Helper: patch text content if both nodes exist.
  function patchText(newRoot, liveRoot, sel) {
    if (!newRoot || !liveRoot) return;
    const a = newRoot.querySelector(sel);
    const b = liveRoot.querySelector(sel);
    if (!a || !b) return;
    const next = a.textContent.trim();
    if (b.textContent.trim() !== next) b.textContent = next;
  }

  // Helper: patch innerHTML if changed.
  function patchHTML(newRoot, liveRoot, sel) {
    if (!newRoot || !liveRoot) return;
    const a = newRoot.querySelector(sel);
    const b = liveRoot.querySelector(sel);
    if (!a || !b) return;
    const next = a.innerHTML.trim();
    if (b.innerHTML.trim() !== next) b.innerHTML = next;
  }

  // Helper: patch className verbatim (handles is-success / is-active toggles).
  function patchClass(newRoot, liveRoot, sel) {
    if (!newRoot || !liveRoot) return;
    const a = newRoot.querySelector(sel);
    const b = liveRoot.querySelector(sel);
    if (!a || !b) return;
    if (a.className !== b.className) b.className = a.className;
  }

  // Helper: patch the [hidden] attribute.
  function patchHidden(newRoot, liveRoot, sel) {
    if (!newRoot || !liveRoot) return;
    const a = newRoot.querySelector(sel);
    const b = liveRoot.querySelector(sel);
    if (!a || !b) return;
    if (a.hidden !== b.hidden) b.hidden = a.hidden;
  }

  // ---------- Progress block: in-place update ----------
  // The CSS transition on .cd-v2-progress__fill { width } animates this.
  function patchProgress(doc) {
    const incoming = doc.querySelector('[data-api-progress]');
    if (!incoming) return;
    // The data-api-progress div wraps the actual snippet output.
    const newProgress = incoming.querySelector('[data-cd-v2-progress]');
    const liveProgress = document.querySelector('[data-cd-v2-progress]');
    if (!newProgress || !liveProgress) return;

    // Shipping line: class (controls is-success green) + innerHTML (text + amount span).
    patchClass(newProgress, liveProgress, '[data-cd-v2-shipping]');
    patchHTML(newProgress, liveProgress, '[data-cd-v2-shipping]');

    // Discount block visibility (hidden when no swim items).
    const newDisc = newProgress.querySelector('[data-cd-v2-discount]');
    const liveDisc = liveProgress.querySelector('[data-cd-v2-discount]');
    if (newDisc && liveDisc) {
      if (newDisc.hidden !== liveDisc.hidden) liveDisc.hidden = newDisc.hidden;
    }

    // Message HTML (contains the green is-success span).
    patchHTML(newProgress, liveProgress, '[data-cd-v2-message]');

    // Counter text ("2 of 3").
    patchText(newProgress, liveProgress, '[data-cd-v2-counter]');

    // Fill bar width — the only thing whose CSS transition we care about.
    const newFill = newProgress.querySelector('[data-cd-v2-fill]');
    const liveFill = liveProgress.querySelector('[data-cd-v2-fill]');
    if (newFill && liveFill) {
      const newWidth = newFill.style.width;
      if (newWidth && liveFill.style.width !== newWidth) {
        liveFill.style.width = newWidth;
      }
    }

    // Milestone dots: just toggle is-active (CSS transitions colour).
    liveProgress.querySelectorAll('[data-cd-v2-milestone]').forEach(liveM => {
      const newM = newProgress.querySelector('[data-cd-v2-milestone="' + liveM.dataset.cdV2Milestone + '"]');
      if (!newM) return;
      const wantActive = newM.classList.contains('is-active');
      if (liveM.classList.contains('is-active') !== wantActive) {
        liveM.classList.toggle('is-active', wantActive);
      }
    });

    // Labels under the bar: is-active toggle, plus text.
    // Text matters now that sale mode swaps the percentages (30/40 vs 5/10):
    // it cannot go stale from cart activity, but editing a tier percent in
    // the theme editor would otherwise leave the label disagreeing with the
    // (patched) message until a reload.
    liveProgress.querySelectorAll('.cd-v2-progress__label').forEach(liveL => {
      const newL = newProgress.querySelector('.cd-v2-progress__label[data-label="' + liveL.dataset.label + '"]');
      if (!newL) return;
      const wantActive = newL.classList.contains('is-active');
      if (liveL.classList.contains('is-active') !== wantActive) {
        liveL.classList.toggle('is-active', wantActive);
      }
      if (liveL.textContent.trim() !== newL.textContent.trim()) {
        liveL.textContent = newL.textContent;
      }
    });

    // Sale mode carries an `is-sale` class and inline --cd-* colour overrides
    // on both the root and the discount block. Same reasoning as the label
    // text: constant per settings state, but keep them honest in the editor.
    // NOTE: setting style/class on these two containers never touches the
    // countdown subtree inside, so the ticking digits are unaffected.
    syncAttr(newProgress, liveProgress, 'style');
    syncAttr(newProgress, liveProgress, 'class');
    const newDisc2 = newProgress.querySelector('[data-cd-v2-discount]');
    const liveDisc2 = liveProgress.querySelector('[data-cd-v2-discount]');
    if (newDisc2 && liveDisc2) {
      syncAttr(newDisc2, liveDisc2, 'style');
    }
  }

  // Copy one attribute across if it differs. Removing when absent matters:
  // turning the sale off must drop the colour overrides, not leave them.
  function syncAttr(newEl, liveEl, name) {
    if (!newEl || !liveEl) return;
    const want = newEl.getAttribute(name);
    const have = liveEl.getAttribute(name);
    if (want === have) return;
    if (want === null) liveEl.removeAttribute(name);
    else liveEl.setAttribute(name, want);
  }

  // ---------- Pair It With: replace contents + visibility ----------
  // Phase 1A: contents are static placeholders so this mostly idempotent;
  // becomes load-bearing in Phase 3 when Rebuy can change the recommendation
  // set on every cart update.
  function patchPair(doc) {
    const incoming = doc.querySelector('[data-api-pair]');
    if (!incoming) return;
    const newPair = incoming.querySelector('[data-cd-v2-pair]');
    const livePair = document.querySelector('[data-cd-v2-pair]');
    if (!livePair) return;
    if (!newPair) {
      livePair.hidden = true;
      return;
    }
    livePair.hidden = newPair.hidden;
    if (livePair.innerHTML !== newPair.innerHTML) {
      livePair.innerHTML = newPair.innerHTML;
    }
  }

  // ---------- Accessory module: replace contents + visibility ----------
  // Carousel candidates change as the user adds/removes accessories
  // (already-in-cart variants get filtered out server-side). We must swap
  // the inner cards + dots, not just toggle the section's hidden attribute.
  // Using innerHTML avoids DOM-node-move issues if the same parsed doc is
  // shared between concurrent refresh() calls (inflight dedup).
  function patchAccessory(doc) {
    const incoming = doc.querySelector('[data-api-accessory]');
    if (!incoming) return;
    const newAcc = incoming.querySelector('[data-cd-v2-accessory]');
    const liveAcc = document.querySelector('[data-cd-v2-accessory]');
    if (!liveAcc) return;
    if (!newAcc) {
      liveAcc.hidden = true;
      return;
    }
    liveAcc.hidden = newAcc.hidden;
    if (liveAcc.innerHTML !== newAcc.innerHTML) {
      liveAcc.innerHTML = newAcc.innerHTML;
    }
  }

  // ---------- Footer savings line ----------
  function patchSavings(doc) {
    const incoming = doc.querySelector('[data-api-savings]');
    const live = document.querySelector('[data-cd-v2-savings]');
    if (!incoming || !live) return;
    const next = incoming.innerHTML.trim();
    if (live.innerHTML.trim() !== next) live.innerHTML = next;
  }

  function refresh() {
    fetchSection()
      .then(doc => {
        try { patchProgress(doc); }  catch (e) { /* noop */ }
        try { patchPair(doc); }      catch (e) { /* noop */ }
        try { patchAccessory(doc); } catch (e) { /* noop */ }
        try { patchSavings(doc); }   catch (e) { /* noop */ }
      })
      .catch(() => { /* live update is best-effort */ });
  }

  document.addEventListener('theme:cart:update', refresh);
  document.addEventListener('theme:cart:added',  refresh);

  // ---------- Sale countdown ----------
  // Counts down to a fixed instant. The Liquid resolves the merchant's local
  // "YYYY-MM-DD HH:MM" to a UTC epoch using the shop's timezone (so DST is
  // handled and every visitor targets the same real moment); we just render
  // the difference against the visitor's own clock.
  //
  // Deliberately re-queries the DOM on every tick rather than holding a node
  // reference: theme.dev.js's renderCartDrawer() does
  // `cartDrawer.innerHTML = template.innerHTML`, which would detach any node
  // we cached. Re-querying makes the ticker self-healing across drawer
  // rebuilds with no rebinding, and a no-op when the countdown isn't rendered.
  //
  // patchProgress never touches this subtree — see the note in the Liquid.
  function cdv2Tick() {
    const el = document.querySelector('[data-cd-v2-countdown]');
    if (!el) return;

    const end = parseInt(el.dataset.cdEnd, 10);
    if (!end) return;

    const timeEl = el.querySelector('[data-cd-v2-countdown-time]');
    if (!timeEl) return;

    const remain = end - Math.floor(Date.now() / 1000);

    if (remain <= 0) {
      const expired = el.dataset.cdExpiredText || '';
      if (expired) {
        if (timeEl.textContent !== expired) timeEl.textContent = expired;
      } else if (!el.hidden) {
        el.hidden = true;
      }
      return;
    }

    // Hours are uncapped on purpose, so a multi-day sale reads e.g. 72:00:00.
    const h = Math.floor(remain / 3600);
    const m = Math.floor((remain % 3600) / 60);
    const s = remain % 60;
    const str = h + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
    if (timeEl.textContent !== str) timeEl.textContent = str;
  }

  setInterval(cdv2Tick, 1000);
  cdv2Tick();

  // No custom add-to-cart handler. The "Don't Forget" Add button uses the
  // theme's native [data-add-to-cart] attribute, which routes through
  // theme.dev.js's CartDrawer.cartAddEvent → addToCart() → getCart() →
  // build() — the same battle-tested path the rest of the site uses for ATC.

  // ---------- Don't Forget carousel: dot indicators ----------
  // When there are 2+ accessory cards, dots track the active card via
  // IntersectionObserver on the scroll container. Click a dot to jump.
  // Re-runs on every cart update because the row may have been
  // re-rendered server-side (different filtering, swap, etc.).
  function wireAccessoryDots() {
    const rows = document.querySelectorAll('[data-cd-v2-accessory-row]');
    rows.forEach(row => {
      if (row.__cdv2DotsWired) return;
      row.__cdv2DotsWired = true;

      const dotsWrap = row.parentElement.querySelector('[data-cd-v2-accessory-dots]');
      if (!dotsWrap) return;
      const dots  = Array.from(dotsWrap.querySelectorAll('[data-cd-v2-accessory-dot]'));
      const cards = Array.from(row.querySelectorAll('[data-cd-v2-accessory-card]'));
      if (dots.length === 0 || cards.length <= 1) return;

      function setActive(idx) {
        dots.forEach((dot, i) => {
          const active = i === idx;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', active ? 'true' : 'false');
        });
      }

      // IntersectionObserver tracks which card is most visible inside the row.
      const io = new IntersectionObserver((entries) => {
        let best = null;
        entries.forEach(entry => {
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        });
        if (best && best.intersectionRatio >= 0.5) {
          const idx = cards.indexOf(best.target);
          if (idx !== -1) setActive(idx);
        }
      }, { root: row, threshold: [0.5, 0.75, 1] });

      cards.forEach(c => io.observe(c));

      // Click a dot to scroll to that card.
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          cards[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          setActive(i);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireAccessoryDots);
  } else {
    wireAccessoryDots();
  }
  // The accessory section gets re-rendered on each cart update by the
  // server-side patch in refresh(); re-wire the dots after that runs.
  document.addEventListener('theme:cart:update', () => setTimeout(wireAccessoryDots, 50));
  document.addEventListener('theme:cart:added',  () => setTimeout(wireAccessoryDots, 50));

  // ---------- Don't Forget COMPACT carousel: transform-driven filmstrip ----------
  // ROOT CAUSE (confirmed via Safari Web Inspector on a real iPhone):
  //   The cart drawer slides in via a CSS `transform` on `.cart-drawer`.
  //   iOS WebKit (so Safari AND Chrome on iOS) has a bug where NATIVE touch
  //   overflow-scrolling inside a transformed ancestor refuses to bind a
  //   fresh reverse-direction touch. Diagnostics:
  //     - Forward swipe scrolls; fresh reverse swipe fires touchstart + ~80
  //       touchmoves (finger moves 180px) but scrollLeft stays frozen.
  //     - Test A: `row.scrollLeft = N` programmatically works fine.
  //     - With touch-action: pan-y (native horizontal scroll OFF) there was
  //       NO horizontal scroll at all — proving native scroll was the only
  //       thing ever moving the row, and that scrollLeft writes during an
  //       active touch are themselves ignored by WebKit here.
  //   Conclusion: the native scroll engine is unusable for this element on
  //   iOS. So we abandon scrollLeft entirely and move an inner track with
  //   `transform: translateX()` — transforms are immune to the scroll bug
  //   (this is how Swiper / Embla / Flickity avoid it).
  //
  // Wiring is DELEGATED on the document so it can never miss the row due to
  // DOM timing (the accessory section is re-rendered server-side on cart
  // updates, swapping the element out). touchstart is passive + delegated;
  // when it lands inside a compact row we begin a drag and attach a
  // NON-passive touchmove (needed to preventDefault the page from scrolling
  // and to own the horizontal gesture) plus touchend/cancel, all removed
  // when the drag ends so the non-passive listener is only live mid-drag.
  //
  // Axis lock at 6px: horizontal → we own it (preventDefault + translate +
  // momentum); vertical → we bail so the drawer body scrolls natively
  // (touch-action: pan-y permits that). Taps never cross 6px, so the + add
  // buttons and product links keep working. Compact carousel only.
  let cdv2Drag = null;            // active touch-drag state, or null
  let cdv2Mom = null;             // momentum state { track, maxOffset, velocity }, or null
  let cdv2MomRAF = null;

  function cdv2GetOffset(track) {
    const m = /translateX\(\s*(-?[\d.]+)px\s*\)/.exec(track.style.transform || '');
    return m ? -parseFloat(m[1]) : 0;   // stored as a positive scroll offset
  }
  function cdv2SetOffset(track, offset) {
    track.style.transform = 'translateX(' + (-offset) + 'px)';
  }
  // Returns the max translate offset, or null if the row isn't laid out
  // enough to measure reliably. Guards against a bad reading at touchstart
  // (clientWidth occasionally measures ~0 there, which would yield a maxOffset
  // large enough to scroll every card off the left edge). Callers keep their
  // previous good value when this returns null, and cdv2OnMove recomputes on
  // every move once layout is settled.
  function cdv2MaxOffset(row, track) {
    const cs = getComputedStyle(row);
    const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    const visible = row.clientWidth - padX;
    if (!(visible > 80)) return null;   // not laid out / unreliable measurement
    return Math.max(0, track.offsetWidth - visible);
  }
  function cdv2CancelMomentum() {
    cdv2Mom = null;
    if (cdv2MomRAF) { cancelAnimationFrame(cdv2MomRAF); cdv2MomRAF = null; }
  }
  function cdv2MomentumStep() {
    if (!cdv2Mom) { cdv2MomRAF = null; return; }
    const m = cdv2Mom;
    if (Math.abs(m.velocity) < 0.4) { cdv2Mom = null; cdv2MomRAF = null; return; }
    let off = cdv2GetOffset(m.track) - m.velocity;
    if (off < 0)             { off = 0; m.velocity = 0; }
    else if (off > m.maxOffset) { off = m.maxOffset; m.velocity = 0; }
    cdv2SetOffset(m.track, off);
    m.velocity *= 0.93;          // per-frame decay → natural glide
    cdv2MomRAF = requestAnimationFrame(cdv2MomentumStep);
  }

  function cdv2OnMove(e) {
    if (!cdv2Drag) return;
    const t = e.touches[0];
    const dx = t.clientX - cdv2Drag.startX;
    const dy = t.clientY - cdv2Drag.startY;

    if (cdv2Drag.axis === null) {
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
        cdv2Drag.axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (cdv2Drag.axis === 'v') { cdv2EndDrag(); return; }  // let native vertical run
      } else {
        return;
      }
    }

    // Horizontal: own it. preventDefault stops the page/drawer also moving;
    // translate the track to the clamped offset.
    e.preventDefault();
    // Recompute maxOffset from the now-settled layout (touchstart's reading
    // can be unreliable). Keep the last good value if measurement fails.
    const mo = cdv2MaxOffset(cdv2Drag.row, cdv2Drag.track);
    if (mo !== null) cdv2Drag.maxOffset = mo;
    let off = cdv2Drag.startOffset - dx;
    off = Math.max(0, Math.min(cdv2Drag.maxOffset, off));
    cdv2SetOffset(cdv2Drag.track, off);

    const now = performance.now();
    const dt = now - cdv2Drag.lastT;
    if (dt > 0) cdv2Drag.velocity = (t.clientX - cdv2Drag.lastX) / dt * 16;
    cdv2Drag.lastX = t.clientX;
    cdv2Drag.lastT = now;
  }

  function cdv2EndDrag() {
    document.removeEventListener('touchmove', cdv2OnMove, { passive: false });
    document.removeEventListener('touchend', cdv2EndDrag);
    document.removeEventListener('touchcancel', cdv2EndDrag);
    if (cdv2Drag && cdv2Drag.axis === 'h' && Math.abs(cdv2Drag.velocity) > 0.5) {
      cdv2Mom = { track: cdv2Drag.track, maxOffset: cdv2Drag.maxOffset, velocity: cdv2Drag.velocity };
      cdv2MomRAF = requestAnimationFrame(cdv2MomentumStep);
    }
    cdv2Drag = null;
  }

  document.addEventListener('touchstart', (e) => {
    const row = e.target.closest && e.target.closest('.cd-v2-accessory--compact [data-cd-v2-accessory-row]');
    if (!row) return;
    const track = row.querySelector('[data-cd-v2-accessory-track]');
    if (!track) return;

    cdv2CancelMomentum();
    const t = e.touches[0];
    cdv2Drag = {
      row, track,
      // May be null here if the row isn't measurable yet; cdv2OnMove
      // recomputes on the first move once layout is settled. Default 0 so a
      // bad initial reading can never permit a runaway over-scroll.
      maxOffset: cdv2MaxOffset(row, track) || 0,
      startX: t.clientX, startY: t.clientY,
      startOffset: cdv2GetOffset(track),
      axis: null,
      lastX: t.clientX, lastT: performance.now(), velocity: 0,
    };
    // Non-passive touchmove only while a drag is in progress, so we don't
    // tax page-wide scroll performance. Listeners live on the document so
    // DOM re-renders of the accessory section can't leave the row unwired.
    document.addEventListener('touchmove', cdv2OnMove, { passive: false });
    document.addEventListener('touchend', cdv2EndDrag);
    document.addEventListener('touchcancel', cdv2EndDrag);
  }, { passive: true });

  // ---------- Don't Forget COMPACT carousel: DESKTOP input ----------
  // The transform mechanism above only handles touch. Desktop never fires
  // touch events, and the row is overflow:hidden (no native scroll), so we
  // add the desktop equivalents here, driving the same track transform:
  //   - wheel: trackpad two-finger horizontal swipe (and horizontal mouse
  //     wheels). Vertical wheels are ignored so the drawer body scrolls.
  //   - mouse click-and-drag: press, move, release.
  // All clamped via the same cdv2MaxOffset / cdv2GetOffset / cdv2SetOffset.
  let cdv2Mouse = null;
  let cdv2SuppressClick = false;

  document.addEventListener('wheel', (e) => {
    const row = e.target.closest && e.target.closest('.cd-v2-accessory--compact [data-cd-v2-accessory-row]');
    if (!row) return;
    const track = row.querySelector('[data-cd-v2-accessory-track]');
    if (!track) return;
    // Only claim predominantly-horizontal wheels; a vertical wheel keeps
    // scrolling the drawer body.
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    const max = cdv2MaxOffset(row, track);
    if (max === null) return;
    const current = cdv2GetOffset(track);
    const next = Math.max(0, Math.min(max, current + e.deltaX));
    if (next !== current) {
      e.preventDefault();         // only when we actually move, so end-of-row
      cdv2SetOffset(track, next); // wheels can still bubble if desired
    }
  }, { passive: false });

  document.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;   // left button only
    const row = e.target.closest && e.target.closest('.cd-v2-accessory--compact [data-cd-v2-accessory-row]');
    if (!row) return;
    const track = row.querySelector('[data-cd-v2-accessory-track]');
    if (!track) return;
    cdv2Mouse = {
      row, track,
      maxOffset: cdv2MaxOffset(row, track) || 0,
      startX: e.clientX,
      startOffset: cdv2GetOffset(track),
      moved: false,
    };
  });

  document.addEventListener('mousemove', (e) => {
    if (!cdv2Mouse) return;
    const d = cdv2Mouse;
    const dx = e.clientX - d.startX;
    if (!d.moved) {
      if (Math.abs(dx) < 4) return;        // threshold so plain clicks still fire
      d.moved = true;
      document.body.style.userSelect = 'none';
    }
    e.preventDefault();
    const mo = cdv2MaxOffset(d.row, d.track);
    if (mo !== null) d.maxOffset = mo;
    cdv2SetOffset(d.track, Math.max(0, Math.min(d.maxOffset, d.startOffset - dx)));
  });

  document.addEventListener('mouseup', () => {
    if (cdv2Mouse && cdv2Mouse.moved) {
      document.body.style.userSelect = '';
      cdv2SuppressClick = true;            // swallow the click that ends a drag
    }
    cdv2Mouse = null;
  });

  // A click fired at the end of a drag would otherwise open the dragged-over
  // product link; suppress just that one.
  document.addEventListener('click', (e) => {
    if (!cdv2SuppressClick) return;
    cdv2SuppressClick = false;
    const row = e.target.closest && e.target.closest('.cd-v2-accessory--compact [data-cd-v2-accessory-row]');
    if (row) { e.preventDefault(); e.stopPropagation(); }
  }, true);

  // ---------- Help overlay: open/close + accordion ----------
  // The overlay slides in from the right covering only the .cd-v2-stage
  // (header + body + trigger row). The footer is structurally outside
  // the stage so it stays in place — no JS-computed offset needed.
  // Accordion is single-open (opening one closes the others). Escape key
  // closes. Focus moves into the overlay on open and back on close.
  let lastFocusBeforeHelp = null;

  function openHelp(drawer, trigger, overlay) {
    if (!drawer || !overlay) return;
    lastFocusBeforeHelp = document.activeElement;
    drawer.classList.add('is-help-open');
    overlay.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    // Move focus to the close button so screen-reader users land at a
    // discoverable affordance, not in the middle of accordion topics.
    // preventScroll: true so the browser doesn't auto-scroll the focused
    // element into view (which can cause perceived motion in the cart
    // body during the overlay's slide animation).
    const closeBtn = overlay.querySelector('[data-cd-v2-help-close]');
    if (closeBtn) {
      try { closeBtn.focus({ preventScroll: true }); }
      catch (e) { closeBtn.focus(); }
    }
  }

  function closeHelp(drawer, trigger, overlay) {
    if (!drawer || !overlay) return;
    drawer.classList.remove('is-help-open');
    overlay.setAttribute('aria-hidden', 'true');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    // Reset accordion state on close so the next open is fresh.
    overlay.querySelectorAll('[data-cd-v2-help-topic]').forEach(closeTopic);
    // Return focus to the trigger. preventScroll for the same reason as
    // openHelp — avoid auto-scroll triggering perceived motion.
    const restoreTo = (lastFocusBeforeHelp && typeof lastFocusBeforeHelp.focus === 'function') ? lastFocusBeforeHelp : trigger;
    if (restoreTo) {
      try { restoreTo.focus({ preventScroll: true }); }
      catch (e) { restoreTo.focus(); }
    }
  }

  function openTopic(topic) {
    const title = topic.querySelector('[data-cd-v2-help-topic-title]');
    const body  = topic.querySelector('[data-cd-v2-help-topic-body]');
    if (!title || !body) return;
    topic.classList.add('is-open');
    title.setAttribute('aria-expanded', 'true');
    body.setAttribute('aria-hidden', 'false');
    body.style.maxHeight = body.scrollHeight + 'px';
  }

  function closeTopic(topic) {
    const title = topic.querySelector('[data-cd-v2-help-topic-title]');
    const body  = topic.querySelector('[data-cd-v2-help-topic-body]');
    if (!title || !body) return;
    topic.classList.remove('is-open');
    title.setAttribute('aria-expanded', 'false');
    body.setAttribute('aria-hidden', 'true');
    body.style.maxHeight = '';
  }

  function wireHelp() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || drawer.__cdv2HelpWired) return;
    drawer.__cdv2HelpWired = true;

    // Open via trigger row
    drawer.addEventListener('click', (e) => {
      const trig = e.target.closest && e.target.closest('[data-cd-v2-help-trigger]');
      if (trig) {
        const overlay = drawer.querySelector('[data-cd-v2-help-overlay]');
        openHelp(drawer, trig, overlay);
        return;
      }
      const closeBtn = e.target.closest && e.target.closest('[data-cd-v2-help-close]');
      if (closeBtn) {
        const overlay = closeBtn.closest('[data-cd-v2-help-overlay]');
        const trig = drawer.querySelector('[data-cd-v2-help-trigger]');
        closeHelp(drawer, trig, overlay);
        return;
      }
      // Accordion toggle (single-open)
      const topicTitle = e.target.closest && e.target.closest('[data-cd-v2-help-topic-title]');
      if (topicTitle) {
        const overlay = topicTitle.closest('[data-cd-v2-help-overlay]');
        if (!overlay) return;
        const topic = topicTitle.closest('[data-cd-v2-help-topic]');
        const wasOpen = topic.classList.contains('is-open');
        // Close all first (single-open accordion)
        overlay.querySelectorAll('[data-cd-v2-help-topic]').forEach(closeTopic);
        if (!wasOpen) openTopic(topic);
      }
    });

    // Escape closes overlay (only when open)
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!drawer.classList.contains('is-help-open')) return;
      const overlay = drawer.querySelector('[data-cd-v2-help-overlay]');
      const trig = drawer.querySelector('[data-cd-v2-help-trigger]');
      closeHelp(drawer, trig, overlay);
    });

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireHelp);
  } else {
    wireHelp();
  }
})();
